import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import {
  propertyActionTypes as types,
  LoadWorkspaceUserPropertiesAction,
  LoadWorkspaceUserPropertiesSuccessAction,
  LoadWorkspaceUserPropertiesFailsAction,
  LoadSelectedPropertyAction,
  LoadSelectedPropertySuccessAction,
  LoadSelectedPropertyFailsAction,
  PropertyInfoChangedAction,
  LoadPropertyTypesAction,
  LoadPropertyTypesSuccessAction,
  LoadPropertyTypesFailsAction,
  DeletePropertyAction,
  DeletePropertySuccessAction,
  DeletePropertyFailsAction,
  LoadEligibleNewPropertyWorkspacesSuccessAction,
  LoadEligibleNewPropertyWorkspacesFailsAction,
  LoadEligibleNewPropertyWorkspacesAction,
  PropertyCreateAction,
  PropertyCreateSuccessAction,
  PropertyCreateFailureAction,
  LoadPropertyFinancialAction,
  LoadPropertyFinancialSuccessAction,
  LoadPropertyFinancialFailsAction,
  ReconcilePropertyFiscalYearAction,
  ReconcilePropertyFiscalYearSuccessAction,
  ReconcilePropertyFiscalYearFailsAction,
  LoadDefaultFiscalYearAction,
  SetSelectedFiscalYearAction,
  SavePropertyDetailsAction,
  SavePropertyDetailsSuccessAction,
  SavePropertyDetailsFailsAction,
  RemovePropertyProfileImageAction,
  RemovePropertyProfileImageSuccessAction,
  RemovePropertyProfileImageFailsAction,
  LoadSelectedPropertyLatLongAction,
  LoadSelectedPropertyLatLongSuccessAction,
  LoadSelectedPropertyLatLongFailsAction,
  LoadPropertiesAction,
  LoadPropertiesSuccessAction,
  LoadPropertiesFailsAction,
  GetAllPropertyReserveStudyAction,
  GetAllPropertyReserveStudySuccessAction,
  GetAllPropertyReserveStudyFailsAction,
  AddPropertyReserveStudyAction,
  AddPropertyReserveStudySuccessAction,
  AddPropertyReserveStudyFailsAction,
  UpdatePropertyReserveStudyAction,
  UpdatePropertyReserveStudySuccessAction,
  UpdatePropertyReserveStudyFailsAction,
  DeletePropertyReserveStudyAction,
  DeletePropertyReserveStudySuccessAction,
  DeletePropertyReserveStudyFailsAction,
  UpdatePropertyFinancialAction,
  UpdatePropertyFinancialSuccessAction,
  UpdatePropertyFinancialFailsAction
} from './property.actions';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { LoadPropertyRequest, Property, PropertyType, PropertyFinancial, PropertyReserveStudy } from '../shared/models';
import { Workspace, WorkspaceUserProperty, FiscalYear } from '../../shared';
import { Router } from '@angular/router';
import { Action } from '@ngrx/store';
import { LoadPropertyResponse } from '../shared/models/load-property-response';
import { PropertyService } from '../shared/services/property.service';
import { ValidateWorkspaceAction } from '../../../shared/auth/redux/auth.actions';
import { ClearAllReportsFromStateAction, ClearFinancialHealthReportFromStateAction, ClearRealTimeFinancialHealthReportFromStateAction } from 'app/internal/reports/redux/reports.actions';
import { ClearFilterSettingsFromStateAction } from 'app/internal/shared/filter-settings-store/filter-settings.actions';
import { ClearComponentsAndCategories } from '../../component/component-store';
import { ClearPropertyPhotos } from 'app/internal/photos/photo-store';
import { PropertySearchFilter, PropertySearchRequest, PropertySearchResult } from '../../user/models';
import { ToastrService } from 'ngx-toastr';
import * as MaintenanceActions from '../../maintenance/maintenance-store/actions/maintenance.actions';
import * as PortfolioAnalyticsActions from '../../portfolio-analytics/store/actions/portfolio-analytics.actions';

@Injectable()
export class PropertyEffects {

  constructor(
    private actions$: Actions,
    private router: Router,
    private toastr: ToastrService,
    private propertyService: PropertyService) { }

  // PROPETY-CREATE
  createProperty$ = createEffect(() => this.actions$.pipe(
    ofType(types.PROPERTY_CREATE),
    map(action => (action as PropertyCreateAction).payload),
    mergeMap(payload =>
      this.propertyService.addProperty(payload)
        .pipe(
          map((response: Property) => new PropertyCreateSuccessAction(response)),
          catchError((error) => of(new PropertyCreateFailureAction(error)))
        )
    )
  )
  );

  createPropertySuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.PROPERTY_CREATE_SUCCESS),
    map(action => (action as PropertyCreateSuccessAction).payload),
    tap((payload: Property) => {
      this.propertyService.setSelectedPropertyId(payload.propertyId);
      this.router.navigateByUrl('/useraccount/properties/dashboard');
    }),
    map((response: any) => new LoadWorkspaceUserPropertiesAction(response))
  )
  );

  savePropertyDetails$ = createEffect(() => this.actions$.pipe(
    ofType(types.SAVE_PROPERTY_DETAILS),
    map(action => (action as SavePropertyDetailsAction).payload),
    mergeMap(payload =>
      this.propertyService.savePropertyDetails(payload.property)
        .pipe(
          map((response: Property) =>
            new SavePropertyDetailsSuccessAction({ property: response, ignoreSetSelectedPropertyId: payload.ignoreSetSelectedPropertyId })),
          catchError((error) => of(new SavePropertyDetailsFailsAction(error)))
        )
    )
  )
  );

  savePropertyDetailsSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.SAVE_PROPERTY_DETAILS_SUCCESS),
    map(action => (action as SavePropertyDetailsSuccessAction).payload),
    tap((payload) => {
      if (!payload.ignoreSetSelectedPropertyId) {
        this.propertyService.setSelectedPropertyId(payload.property.propertyId);
      }
    }),
    mergeMap((payload) => {
      return [
        // This loads the (updated) selected property & its details
        new PropertyInfoChangedAction(payload.property.propertyId),

        // This will refresh the data that is used to populate the Property drop down list
        new LoadWorkspaceUserPropertiesAction()
      ];
    }),
  )
  );

  removePropertyProfileImage$ = createEffect(() => this.actions$.pipe(
    ofType(types.REMOVE_PROPERTY_PROFILE_IMAGE),
    map(action => (action as RemovePropertyProfileImageAction).payload),
    mergeMap(propertyId =>
      this.propertyService.removePropertyProfileImage(propertyId)
        .pipe(
          map((response: any) => new RemovePropertyProfileImageSuccessAction(response)),
          catchError((error) => of(new RemovePropertyProfileImageFailsAction(error)))
        )
    )
  )
  );

  loadWorkspaceUserProperties$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_WorkspaceUserProperties),
    map((action: LoadWorkspaceUserPropertiesAction) => action.payload),
    mergeMap(() =>
      this.propertyService.getAllProperties()
        .pipe(
          map((response: WorkspaceUserProperty[]) => new LoadWorkspaceUserPropertiesSuccessAction(response)),
          catchError(error => of(new LoadWorkspaceUserPropertiesFailsAction(error)))
        )
    )
  )
  );

  loadWorkspaceUserPropertiesSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_WorkspaceUserProperties_SUCCESS),
    map(() => this.propertyService.getSelectedPropertyId()),
    map((response: string) => new LoadSelectedPropertyAction(new LoadPropertyRequest(response)))
  ));

  loadSelectedProperty$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_Selected_Property),
    map((action: LoadSelectedPropertyAction) => action.payload),
    mergeMap((request) => {

      if (request.propertyId) {
        return this.propertyService.loadPropertyById(request.propertyId)
          .pipe(
            map((response: Property) =>
              new LoadSelectedPropertySuccessAction(
                new LoadPropertyResponse(response, request.isRedirect, request.url, request.ignoreValidateWorkspace))),
            catchError((error) => of(new LoadSelectedPropertyFailsAction(error)))
          );
      } else {
        return of<Action>();
      }
    })
  )
  );

  loadSelectedPropertySuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_Selected_Property_SUCCESS),
    map((action: LoadSelectedPropertySuccessAction) => action.payload),
    tap((response: LoadPropertyResponse) => {
      this.propertyService.setSelectedPropertyId(response.property.propertyId);
      if (response.isRedirect) {
        const url = response.url ? response.url : 'useraccount/properties/dashboard';
        this.router.navigateByUrl(url);
      }
    }),
    mergeMap((response: LoadPropertyResponse) => {
      const actions: any[] = [
        new ClearAllReportsFromStateAction(response),
        new ClearComponentsAndCategories(),
        new ClearFilterSettingsFromStateAction(response),
        new ClearPropertyPhotos(),
        new LoadDefaultFiscalYearAction(response.property),
        new LoadSelectedPropertyLatLongAction(response.property),
      ];

      if (!response.ignoreValidateWorkspace) {
        actions.push(new ValidateWorkspaceAction(response.property.propertyId));
      }

      if (response.isRedirect) {
        actions.push(MaintenanceActions.resetMaintenanceTaskState());
        actions.push(PortfolioAnalyticsActions.resetState());
      }

      return actions;
    }),
  )
  );

  propertyInfoChanged$ = createEffect(() => this.actions$.pipe(
    ofType(types.PROPERTY_INFO_CHANGED),
    map((action: PropertyInfoChangedAction) => action.payload),
    mergeMap(propertyId =>
      this.propertyService.getPropertyById(propertyId)
        .pipe(
          map((response: Property) => new LoadSelectedPropertySuccessAction(new LoadPropertyResponse(response))),
          catchError(error => of(new LoadSelectedPropertyFailsAction(error)))
        )
    )
  )
  );

  loadPropertyTypes$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_PropertyTypes),
    map((action: LoadPropertyTypesAction) => action.payload),
    mergeMap(() =>
      this.propertyService
        .getPropertyTypes()
        .pipe(
          map((response: PropertyType[]) => new LoadPropertyTypesSuccessAction(response)),
          catchError(error => of(new LoadPropertyTypesFailsAction(error)))
        )
    ))
  );

  loadEligibleNewPropertyWorkspaces$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_EligibleNewPropertyWorkspaces),
    map((action: LoadEligibleNewPropertyWorkspacesAction) => action.payload),
    mergeMap(() =>
      this.propertyService
        .getNewPropertyWorkspaces()
        .pipe(
          map((response: Workspace[]) => new LoadEligibleNewPropertyWorkspacesSuccessAction(response)),
          catchError(error => of(new LoadEligibleNewPropertyWorkspacesFailsAction(error)))
        )
    )
  )
  );

  deleteProperty$ = createEffect(() => this.actions$.pipe(
    ofType(types.DELETE_Property),
    map((action: DeletePropertyAction) => action.payload),
    mergeMap(propertyId =>
      this.propertyService
        .deleteProperty(propertyId)
        .pipe(
          mergeMap(response => {
            return [
              new DeletePropertySuccessAction(response),
              new LoadWorkspaceUserPropertiesAction()
            ];
          }),
          catchError(error => of(new DeletePropertyFailsAction(error)))
        )
    )
  )
  );

  deletePropertySuccess$ = createEffect(() => this.actions$.pipe(
    ofType(types.DELETE_Property_SUCCESS),
    map((action: DeletePropertySuccessAction) => action.payload),
    tap(() => {
      this.toastr.success('Property Removed');
    }),
    mergeMap(() => [
      new LoadPropertiesAction(new PropertySearchRequest(new PropertySearchFilter())),
    ]),
  )
  );

  // PROPERTY FINANCIAL
  loadPropertyFinancial$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_PropertyFinancial),
    map((action: LoadPropertyFinancialAction) => action.payload),
    mergeMap((request) =>
      this.propertyService
        .getPropertyFinancialEntries(request.propertyId, request.fiscalYear)
        .pipe(
          map((response: PropertyFinancial) => new LoadPropertyFinancialSuccessAction(response)),
          catchError(error => of(new LoadPropertyFinancialFailsAction(error)))
        )
    ))
  );

  updatePropertyFinancial$ = createEffect(() => this.actions$.pipe(
    ofType(types.UPDATE_PropertyFinancial),
    map((action: UpdatePropertyFinancialAction) => action.payload),
    mergeMap((request) =>
      this.propertyService
        .savePropertyFinancialEntries(request.propertyFinancial, request.fiscalYear, request.isPlanningAssumptions)
        .pipe(
          map((response: PropertyFinancial) => new UpdatePropertyFinancialSuccessAction(response)),
          catchError(error => of(new UpdatePropertyFinancialFailsAction(error)))
        )
    ))
  );

  updatePropertyFinancialSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(types.UPDATE_PropertyFinancial_SUCCESS),
      map((action: UpdatePropertyFinancialSuccessAction) => action.payload),
      tap(() => {
        this.router.navigateByUrl('/useraccount/plans');
      }),
      mergeMap(() => [
        new ClearRealTimeFinancialHealthReportFromStateAction({}),
        new ClearFinancialHealthReportFromStateAction({})
      ]),
    );
  }, { dispatch: true }
  );

  // RECONCILE FISCAL YEAR
  reconcilePropertyFiscalYear$ = createEffect(() => this.actions$.pipe(
    ofType(types.RECONCILE_FiscalYear),
    map(action => (action as ReconcilePropertyFiscalYearAction).payload),
    mergeMap((payload: any) =>
      this.propertyService.reconcileFiscalYear(payload.propertyId, payload.reconcileRequest)
        .pipe(
          map(() => new ReconcilePropertyFiscalYearSuccessAction(payload.reconcileRequest)),
          catchError((error) => {
            // eslint-disable-next-line no-console
            console.log(error);
            return of(new ReconcilePropertyFiscalYearFailsAction(error));
          })
        )
    ))
  );

  // FISCAL YEAR SELECTION
  loadSelectedFiscalYear$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_DEFAULT_FISCAL_YEAR),
    map(action => (action as LoadDefaultFiscalYearAction).payload),
    map((property) => this.propertyService.getDefaultFiscalYear(property)),
    map((response: FiscalYear) => new SetSelectedFiscalYearAction(response))
  )
  );

  setSelectedFiscalYear$ = createEffect(() => this.actions$.pipe(
    ofType(types.SET_SELECTED_FISCAL_YEAR),
    map((action: SetSelectedFiscalYearAction) => action.payload),
    tap((payload: FiscalYear) => {
      this.propertyService.setSelectedFiscalYear(payload);
    })
  ), { dispatch: false }
  );

  // LAT/LONG
  loadSelectedPropertyLatLong$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_SELECTED_PROPERTY_LAT_LONG),
    map((action: LoadSelectedPropertyLatLongAction) => action.payload),
    mergeMap((request: Property) =>

      this.propertyService
        .convertAddressToLatLong(request)
        .pipe(
          map((response: google.maps.LatLngLiteral) => new LoadSelectedPropertyLatLongSuccessAction(response)),
          catchError(error => of(new LoadSelectedPropertyLatLongFailsAction(error)))
        )
    )
  )
  );

  loadProperties$ = createEffect(() => this.actions$.pipe(
    ofType(types.LOAD_PROPERTIES),
    map(action => (action as LoadPropertiesAction).payload),
    tap((payload: PropertySearchRequest) => {
      this.propertyService.setPropertyFilter(payload?.filter);
    }),
    mergeMap((payload: PropertySearchRequest) =>
      this.propertyService.getProperties(payload)
        .pipe(
          map((response: PropertySearchResult) => new LoadPropertiesSuccessAction(response)),
          catchError((error) => of(new LoadPropertiesFailsAction(error)))
        )
    ))
  );


  // RESERVE STUDY HISTORY
  getPropertyReserveStudies$ = createEffect(() => this.actions$.pipe(
    ofType(types.GET_ALL_PROPERTY_RESERVE_STUDY),
    map((action: GetAllPropertyReserveStudyAction) => action.payload),
    mergeMap((payload) =>
      this.propertyService
        .getAllPropertyReserveStudies(payload)
        .pipe(
          map((response: PropertyReserveStudy[]) => new GetAllPropertyReserveStudySuccessAction(response)),
          catchError(error => of(new GetAllPropertyReserveStudyFailsAction(error)))
        )
    )
  ));

  addPropertyReserveStudy$ = createEffect(() => this.actions$.pipe(
    ofType(types.ADD_PROPERTY_RESERVE_STUDY),
    map(action => (action as AddPropertyReserveStudyAction).payload),
    mergeMap(payload =>
      this.propertyService.addPropertyReserveStudy(payload)
        .pipe(
          map((response: PropertyReserveStudy) => new AddPropertyReserveStudySuccessAction(response)),
          catchError((error) => of(new AddPropertyReserveStudyFailsAction(error)))
        )
    )
  ));

  updatePropertyReserveStudy$ = createEffect(() => this.actions$.pipe(
    ofType(types.UPDATE_PROPERTY_RESERVE_STUDY),
    map(action => (action as UpdatePropertyReserveStudyAction).payload),
    mergeMap(payload =>
      this.propertyService.updatePropertyReserveStudy(payload)
        .pipe(
          map((response: PropertyReserveStudy) => new UpdatePropertyReserveStudySuccessAction(response)),
          catchError((error) => of(new UpdatePropertyReserveStudyFailsAction(error)))
        )
    )
  ));

  removePropertyReserveStudy$ = createEffect(() => this.actions$.pipe(
    ofType(types.DELETE_PROPERTY_RESERVE_STUDY),
    map(action => (action as DeletePropertyReserveStudyAction).payload),
    mergeMap(payload =>
      this.propertyService.deletePropertyReserveStudy(payload.propertyId, payload.propertyReserveStudyId)
        .pipe(
          map((response: any) => new DeletePropertyReserveStudySuccessAction(response)),
          catchError((error) => of(new DeletePropertyReserveStudyFailsAction(error)))
        )
    )
  ));
}